import { z } from 'zod';
import { ChannelIdSchema } from './ids.js';
import { ColourSchema, ImageUrlSchema } from './party.js';
import { SpotifyTrackIdSchema } from './spotify.js';
import { ArtistNamesSchema } from './track.js';
const CountSchema = z.number().int().nonnegative();
const DurationMsSchema = z.number().int().nonnegative();
const FractionSchema = z.number().min(0).max(1);
export const WrappedPartySchema = z
    .strictObject({
    displayName: z.string().min(1),
    createdAt: z.iso.datetime(),
    endedAt: z.iso.datetime().nullable(),
})
    .readonly();
/**
 * The party's own channel slots, colour included, so the recap never has to
 * know which colour slot 2 is — a mapping that lives in the party row and
 * would silently go stale if a page hardcoded it.
 *
 * Deliberately no display name: `channels.display_name` is the typed name of
 * whoever currently holds the slot, not a name for the slot, so anything that
 * rendered it here would caption a channel with a person.
 */
export const WrappedChannelSchema = z
    .strictObject({
    slotNumber: ChannelIdSchema,
    colour: ColourSchema,
})
    .readonly();
export const WrappedArtistSchema = z
    .strictObject({
    name: z.string().min(1),
    plays: CountSchema,
    ms: DurationMsSchema,
    slots: z.array(ChannelIdSchema).readonly(),
})
    .readonly();
/** trackId is null for a listen logged before the id was stored, or one Spotify never named. */
export const WrappedTrackSchema = z
    .strictObject({
    trackId: SpotifyTrackIdSchema.nullable(),
    name: z.string().min(1),
    artists: ArtistNamesSchema,
    album: z.string().min(1),
    artUrl: ImageUrlSchema,
    plays: CountSchema,
})
    .readonly();
export const WrappedDriftTrackSchema = z
    .strictObject({
    name: z.string().min(1),
    album: z.string().min(1),
    artUrl: ImageUrlSchema,
})
    .readonly();
// A pair list rather than a record keyed by slot: a Zod record widens the key
// back to `string`, losing the ChannelId brand, and an index signature is
// unreadable under noPropertyAccessFromIndexSignature.
export const WrappedSlotMsSchema = z
    .strictObject({
    slotNumber: ChannelIdSchema,
    ms: DurationMsSchema,
})
    .readonly();
export const WrappedDjSchema = z
    .strictObject({
    name: z.string().min(1),
    ms: DurationMsSchema,
    stints: CountSchema,
    msBySlot: z.array(WrappedSlotMsSchema).readonly(),
})
    .readonly();
export const WrappedStintSchema = z
    .strictObject({
    slotNumber: ChannelIdSchema,
    displayName: z.string().min(1),
    startedAt: z.iso.datetime(),
    endedAt: z.iso.datetime().nullable(),
})
    .readonly();
export const WrappedHourSchema = z
    .strictObject({
    at: z.iso.datetime(),
    plays: CountSchema,
})
    .readonly();
export const WrappedMostWantedSchema = z
    .strictObject({
    title: z.string().min(1),
    artist: z.string().min(1),
    artUrl: ImageUrlSchema,
    wants: CountSchema,
    plays: CountSchema,
})
    .readonly();
export const WrappedCrowdSchema = z
    .strictObject({
    /**
     * Suggestions still on the want list plus those a DJ took — a DJ who
     * dismisses a suggestion hard-deletes it, so the room's rejected asks
     * leave no row and cannot be counted here.
     */
    wants: CountSchema,
    mostWanted: WrappedMostWantedSchema.nullable(),
    playedFraction: FractionSchema,
    avgWaitMs: DurationMsSchema,
})
    .readonly();
export const WrappedFastestSkipSchema = z
    .strictObject({
    trackName: z.string().min(1),
    listenedMs: DurationMsSchema,
    durationMs: z.number().int().positive(),
})
    .readonly();
/**
 * A skip is never recorded: the schema stores how long a track was listened to,
 * not why it stopped. This figure infers one from `listened_ms` under half of
 * `duration_ms`, which cannot tell a DJ pulling a track early from a gap in the
 * playback poll — a missed tick, a rate-limited account or a paused deck all
 * look the same. Read it as "cut short", not as an accusation.
 */
export const WrappedSkipperSchema = z
    .strictObject({
    name: z.string().min(1),
    skipFraction: FractionSchema,
    fastest: WrappedFastestSkipSchema,
})
    .readonly();
export const WrappedEdgeTrackSchema = z
    .strictObject({
    name: z.string().min(1),
    artists: ArtistNamesSchema,
    album: z.string().min(1),
    artUrl: ImageUrlSchema,
    at: z.iso.datetime(),
    dj: z.string().min(1),
    slotNumber: ChannelIdSchema,
})
    .readonly();
export const WRAPPED_TOP_TRACKS_LIMIT = 5;
export const WRAPPED_DRIFT_TRACKS_LIMIT = 14;
/**
 * Everything the recap page draws, pre-aggregated server-side: the page is one
 * read of a finished party, so it cannot afford a request per panel.
 * Null fields are the honest answer for a party that never had the data —
 * nobody suggested anything, nobody played enough to be ranked.
 */
export const PartyWrappedSchema = z
    .strictObject({
    party: WrappedPartySchema,
    channels: z.array(WrappedChannelSchema).readonly(),
    durationMs: DurationMsSchema,
    /** Time actually sounding, summed from the listens — not the wall clock. */
    musicMs: DurationMsSchema,
    djCount: CountSchema,
    /** Phones that got through the passkey, one token each. */
    tokenCount: CountSchema,
    trackCount: CountSchema,
    uniqueTracks: CountSchema,
    uniqueArtists: CountSchema,
    topArtist: WrappedArtistSchema.nullable(),
    topTracks: z.array(WrappedTrackSchema).max(WRAPPED_TOP_TRACKS_LIMIT).readonly(),
    driftTracks: z.array(WrappedDriftTrackSchema).max(WRAPPED_DRIFT_TRACKS_LIMIT).readonly(),
    djs: z.array(WrappedDjSchema).readonly(),
    stints: z.array(WrappedStintSchema).readonly(),
    /** Consecutive stints on one slot held by different DJs. */
    handovers: CountSchema,
    hours: z.array(WrappedHourSchema).readonly(),
    peakHourAt: z.iso.datetime().nullable(),
    crowd: WrappedCrowdSchema,
    skipper: WrappedSkipperSchema.nullable(),
    opener: WrappedEdgeTrackSchema.nullable(),
    closer: WrappedEdgeTrackSchema.nullable(),
})
    .readonly();
//# sourceMappingURL=wrapped.js.map